import React, { useState, useId } from 'react';
import classnames from 'classnames';
import dateFormat from 'dateformat';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import PropTypes from 'prop-types';
import { maxBy, minBy, get, head } from 'lodash-es';
import { formatPriceKString } from 'site-modules/shared/utils/price-utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { TIME } from 'client/utils/time';
import { GraphActiveDot } from './graph-active-dot';

export const PriceHistoryGraph = ({
  currentPrice,
  history = [],
  margin = {
    top: 5,
    right: 30,
    left: 10,
    bottom: 5,
  },
  noCollapse = false,
  firstPublishedDate = 0,
  wrapperClasses = '',
}) => {
  const originalPrice = get(head(history), 'amount');
  const noHistory = history && history.length === 1 && originalPrice === currentPrice;
  const [currentDate] = useState(() => Date.now());
  const randomTooltipInt = useId();

  if (!history.length || noHistory) {
    return null;
  }
  const formatYValues = price => formatPriceKString(price);
  const numberOfFullDays = Math.floor((currentDate - firstPublishedDate) / TIME.MS.ONE_DAY);
  const dateMask = numberOfFullDays < 365 ? 'm/d' : 'm/d/yy';
  const data = [];
  let prevPrice;
  let isLastPriceIncrease;

  history.forEach(({ amount, timestamp }) => {
    data.push({
      name: dateFormat(timestamp, dateMask),
      timestamp,
      amount,
      isPriceIncrease: prevPrice < amount,
      isOriginalPrice: !prevPrice,
      randomTooltipInt,
    });
    isLastPriceIncrease = prevPrice < amount;
    prevPrice = amount;
  });

  const isCurrectPriceIncrease = isLastPriceIncrease && prevPrice === currentPrice;

  data.push({
    name: dateFormat(currentDate, dateMask),
    timestamp: currentDate,
    amount: currentPrice,
    isPriceIncrease: isCurrectPriceIncrease || prevPrice < currentPrice,
    isCurrentPrice: true,
    randomTooltipInt,
  });

  const maxVal = maxBy(data, 'amount').amount;
  const minVal = minBy(data, 'amount').amount;
  const maxPrice = Math.ceil(maxVal / 1000) * 1000;
  const minPrice = Math.floor(minVal / 1000) * 1000;
  const chartParams = {
    xAxis: {
      dataKey: 'name',
      tickLine: false,
      interval: 'preserveStartEnd',
      axisLine: false,
    },
    yAxis: {
      tickLine: false,
      domain: [minPrice, maxPrice],
      interval: 'preserveStartEnd',
      tickFormatter: formatYValues,
      axisLine: false,
    },
    grid: {
      stroke: '#cbd4e1',
    },
    line: {
      type: 'linear',
      dataKey: 'amount',
      stroke: '#64748b',
      isAnimationActive: false,
      dot: <GraphActiveDot />,
      activeDot: false,
    },
  };
  const chart = (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer width={'100%'} height={200} debounce={800} className="mt-1">
        <LineChart height={200} data={data} margin={margin}>
          <CartesianGrid {...chartParams.grid} />
          <XAxis {...chartParams.xAxis} />
          <YAxis {...chartParams.yAxis} />
          <Line {...chartParams.line} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <Row className="price-history-graph px-0_5">
      <Col className={classnames('small', wrapperClasses)}>
        {noCollapse ? (
          chart
        ) : (
          <ContentCollapse
            textClosed="View price history"
            textOpen="Hide price history"
            justify="justify-content-start"
            btnContainerClasses="mt-0_5 pt-0 pb-0_5"
            btnClasses="ms-1_75 px-0_5 py-0 size-16 text-primary-darker more-info-btn"
            btnTextClasses="fw-normal"
            showBtnBelow
            isEclipseFade={false}
            trackingId="view_price_history"
          >
            {chart}
          </ContentCollapse>
        )}
      </Col>
    </Row>
  );
};
PriceHistoryGraph.propTypes = {
  currentPrice: PropTypes.number.isRequired,
  history: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      timestamp: PropTypes.number,
    })
  ),
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number,
  }),
  noCollapse: PropTypes.bool,
  firstPublishedDate: PropTypes.number,
  wrapperClasses: PropTypes.string,
};
